import {
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { DashboardService } from '../../core/services/dashboard.service';
import { EquipmentService } from '../../core/services/equipment.service';
import { ExportService } from '../../core/services/export.service';
import { InventoryService } from '../../core/services/inventory.service';
import { MaintenanceService } from '../../core/services/maintenance.service';
import { environment } from '../../environments/environment';
import { CheckoutRecord } from '../../core/models/equipment.models';
import { InventoryItem, StockSnapshot } from '../../core/models/inventory.models';
import {
  currentTimestamp,
  formatCurrency,
  formatDate,
} from '../../core/utils/format.util';

function todayIso(): string {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

function dateKey(value: string | undefined): string {
  const raw = (value ?? '').trim();
  if (!raw) {
    return '';
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  // Use local calendar day (not UTC) so SA dates match "today".
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function maxCount(...values: number[]): number {
  return Math.max(0, ...values.map((v) => (Number.isFinite(v) ? v : 0)));
}

@Component({
  selector: 'app-dashboard',
  imports: [RouterLink],
  standalone: true,
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
})
export class DashboardComponent implements OnInit, OnDestroy {
  private readonly dashboard = inject(DashboardService);
  private readonly inventory = inject(InventoryService);
  private readonly equipment = inject(EquipmentService);
  private readonly maintenance = inject(MaintenanceService);
  private readonly exporter = inject(ExportService);

  readonly timestamp = currentTimestamp;
  readonly formatCurrency = formatCurrency;
  readonly formatDate = formatDate;
  readonly useLiveApi = !environment.useMockData;

  readonly isLoading = computed(
    () =>
      this.useLiveApi &&
      this.dashboard.isLoading() &&
      this.inventory.allStockSnapshots().length === 0 &&
      this.inventory.allItems().length === 0,
  );

  readonly showToast = signal(false);
  readonly toastMessage = signal('');
  readonly toastType = signal<'success' | 'error' | 'info'>('error');
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  readonly stockSnapshots = computed(() => this.inventory.allStockSnapshots());

  private readonly itemsById = computed(() => {
    const map = new Map<string, InventoryItem>();
    for (const item of this.inventory.allItems()) {
      map.set(item.id, item);
    }
    return map;
  });

  private isNonRentableItem(inventoryItemId?: string): boolean {
    if (!inventoryItemId) {
      return true;
    }
    return this.itemsById().get(inventoryItemId)?.isRentable !== true;
  }

  /** Consumable / non-rentable Register Items (matches Current Stock focus). */
  readonly nonRentableActiveSkus = computed(
    () =>
      this.inventory
        .allItems()
        .filter(
          (item) => item.status === 'active' && item.isRentable !== true,
        ).length,
  );

  readonly lowStockAlerts = computed(() =>
    this.stockSnapshots()
      .filter(
        (row) =>
          row.stockStatus === 'low' &&
          this.isNonRentableItem(row.inventoryItemId),
      )
      .slice(0, 5),
  );

  readonly lowStockCount = computed(
    () =>
      this.stockSnapshots().filter(
        (row) =>
          row.stockStatus === 'low' &&
          this.isNonRentableItem(row.inventoryItemId),
      ).length,
  );

  readonly outOfStockCount = computed(
    () =>
      this.stockSnapshots().filter(
        (row) =>
          this.isNonRentableItem(row.inventoryItemId) &&
          (row.quantity <= 0 || row.stockStatus === 'critical'),
      ).length,
  );

  readonly inventoryValue = computed(() => {
    const items = this.itemsById();
    const fromStock = this.stockSnapshots().reduce((sum, row) => {
      if (!this.isNonRentableItem(row.inventoryItemId)) {
        return sum;
      }
      const catalog = row.inventoryItemId
        ? items.get(row.inventoryItemId)
        : undefined;
      const unit = row.unitValue || catalog?.price || 0;
      return sum + row.quantity * unit;
    }, 0);
    if (fromStock > 0) {
      return fromStock;
    }
    // Fall back to API dashboard total when local unit costs are missing.
    return this.dashboard.stockSummary().totalInventoryValue || 0;
  });

  readonly goodsReceivedToday = computed(() => {
    const today = todayIso();
    const local = this.inventory
      .allGoodsReceived()
      .filter(
        (row) =>
          row.status !== 'cancelled' &&
          dateKey(row.receivedDate) === today,
      ).length;
    const api = this.dashboard.stockSummary().goodsReceivedToday || 0;
    return maxCount(local, api);
  });

  readonly issuesToday = computed(() => {
    const today = todayIso();
    // Count all non-cancelled issues dated today (issued + reserved holds posted today).
    const local = this.inventory
      .issueStockList()
      .filter((row) => dateKey(row.issueDate) === today).length;
    const api = this.dashboard.stockSummary().issuesToday || 0;
    return maxCount(local, api);
  });

  readonly overdueReturns = computed(() =>
    this.equipment.getOverdueCheckouts().slice(0, 5),
  );
  readonly overdueCount = computed(() => {
    const local = this.equipment.stats().overdue;
    return local || this.dashboard.equipmentSummary().overdue || 0;
  });
  /** Due-today / legacy KPI row — use checkout stats, not AssetRegister-only. */
  readonly equipmentStats = computed(() => {
    const page = this.equipment.checkoutPageStats();
    const api = this.dashboard.equipmentSummary();
    const local = this.equipment.stats();
    return {
      available: page.available || api.available || 0,
      checkedOut: page.checkedOut || api.checkedOut || 0,
      dueToday: local.dueToday || api.dueToday || 0,
      overdue: local.overdue || api.overdue || 0,
    };
  });
  readonly totalAssets = computed(() => this.equipment.allAssets().length);

  /** Rentables panel — same sources as Issue Items / Check-In. */
  readonly rentableAvailable = computed(
    () => this.equipment.checkoutPageStats().available,
  );
  readonly rentableCheckedOut = computed(
    () => this.equipment.checkoutPageStats().checkedOut,
  );

  readonly reservedCount = computed(() => {
    const local = this.equipment
      .allReservations()
      .filter(
        (row) =>
          row.status === 'pending' ||
          row.status === 'confirmed' ||
          // Paid and waiting on Check-Out still counts as booked.
          (row.status === 'completed' && !!row.pendingCheckoutId),
      ).length;
    const ops = this.dashboard.operationsSummary();
    const apiOps = ops.pendingReservations + ops.approvedReservations;
    const apiEquipment = this.dashboard.equipmentSummary().reserved || 0;
    return maxCount(local, apiOps, apiEquipment);
  });

  readonly inRepairCount = computed(() => {
    const fromAssets = this.equipment
      .rentableAssets()
      .filter((asset) => asset.status === 'in-repair').length;
    const fromAllAssets = this.equipment
      .allAssets()
      .filter((asset) => asset.status === 'in-repair').length;
    const maintenance = this.maintenance.jobStats();
    const fromJobs = maintenance.pending + maintenance.inProgress;
    const apiEquipment = this.dashboard.equipmentSummary().inRepair || 0;
    const ops = this.dashboard.operationsSummary();
    const apiJobs = ops.openMaintenance + ops.inProgressMaintenance;
    return maxCount(fromAssets, fromAllAssets, fromJobs, apiEquipment, apiJobs);
  });

  readonly damagedCount = computed(
    () => this.equipment.getDamagedRecords().length,
  );

  readonly openMaintenanceCount = computed(() => {
    const stats = this.maintenance.jobStats();
    return stats.pending + stats.inProgress;
  });

  readonly recentIssues = computed(() =>
    this.inventory.issuedOutStock().slice(0, 5),
  );

  readonly recentCheckouts = computed(() =>
    this.equipment
      .allCheckouts()
      .filter((row) => row.status === 'active' || row.status === 'overdue')
      .slice(0, 5),
  );

  readonly recentFeed = computed(() => {
    const issues = this.recentIssues().map((issue) => ({
      kind: 'issue' as const,
      id: `issue-${issue.id}`,
      title: issue.itemName || issue.issueNumber,
      detail: issue.issuedTo,
      date: issue.issueDate,
      route: '/issued-stock',
    }));
    const checkouts = this.recentCheckouts().map((row) => ({
      kind: 'checkout' as const,
      id: `checkout-${row.id}`,
      title: row.equipment,
      detail: row.customer,
      date: row.checkoutDate || row.dueDate,
      route: '/check-in',
    }));
    return [...issues, ...checkouts]
      .sort((a, b) => dateKey(b.date).localeCompare(dateKey(a.date)))
      .slice(0, 8);
  });

  ngOnInit(): void {
    this.refresh();
  }

  ngOnDestroy(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
  }

  refresh(): void {
    if (!environment.useMockData) {
      this.dashboard.load();
      this.inventory.refreshFromApi();
      this.equipment.refreshFromApi();
      this.maintenance.refreshFromApi();
    }
  }

  closeToast(): void {
    this.showToast.set(false);
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
  }

  snapshotLabel(row: StockSnapshot): string {
    return `${row.itemName || row.itemCode} · ${row.location || row.branch || 'Stock'}`;
  }

  checkoutLabel(row: CheckoutRecord): string {
    return `${row.equipment} · ${row.customer}`;
  }

  exportData(): void {
    const rows = [
      ...this.recentIssues().map((issue) => ({
        section: 'Recent issues',
        reference: issue.issueNumber,
        name: issue.issuedTo,
        date: issue.issueDate,
        value: issue.items,
      })),
      ...this.overdueReturns().map((checkout) => ({
        section: 'Overdue returns',
        reference: checkout.checkoutNumber,
        name: checkout.equipment,
        date: checkout.dueDate,
        value: checkout.customer,
      })),
      ...this.lowStockAlerts().map((item) => ({
        section: 'Low stock',
        reference: item.itemCode,
        name: item.itemName,
        date: '',
        value: `${item.quantity} at ${item.location}`,
      })),
    ];

    if (!rows.length) {
      this.openToast('Nothing to export yet.', 'info');
      return;
    }

    this.exporter.downloadCsv(
      `inventory-dashboard-${new Date().toISOString().slice(0, 10)}`,
      rows,
    );
  }

  private openToast(message: string, type: 'success' | 'error' | 'info'): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.toastMessage.set(message);
    this.toastType.set(type);
    this.showToast.set(true);
    this.toastTimer = setTimeout(() => this.closeToast(), 5000);
  }
}
